import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from "react";
import { useGlobalData } from "./TinaContext";
import Autoplay from "embla-carousel-autoplay";

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

import { cn, tinaFieldSafe } from "@/utils";
import SVG from "react-inlinesvg";
import {
  motion,
  useReducedMotion,
  useMotionValue,
  useMotionValueEvent,
  useScroll,
  AnimatePresence,
} from "motion/react";

const NO_FILL_LOGOS = ["Framework"];
const NO_MAX_HEIGHT_LOGOS = ["Cisco"];

/**
 * When true: desktop shows the Framer stagger + popLayout logo row (large screens).
 * Carousel is then `lg:hidden`. When false: carousel at all breakpoints.
 */
const USE_ANIMATED_DESKTOP_LOGO_ROW = false;

/** Viewport divisor for animated row cell count — keep in sync with `lg:basis-[244px]` on carousel slides. */
const LOGOS_WIDTH = 244;
const TARGET_SIZE = 60;

type LogoEntry = {
  image?: string | null;
  altText?: string | null;
};

const NormalizedLogo = ({
  src,
  className,
  title,
}: {
  src: string;
  className?: string;
  title?: string;
}) => {
  const svgRef = useRef<SVGElement>(null);

  const handleLoad = useCallback(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const vb = (svg as SVGSVGElement).viewBox?.baseVal;
    if (!vb?.width || !vb?.height) return;

    const ar = vb.width / vb.height;
    svg.style.maxHeight = `${Math.round(TARGET_SIZE / Math.pow(ar, 0.55))}px`;
  }, []);

  return (
    <SVG
      innerRef={svgRef}
      src={src}
      onLoad={handleLoad}
      preserveAspectRatio="xMidYMid meet"
      className={className}
      title={title}
    />
  );
};

/** Desktop-only stagger row; gated by USE_ANIMATED_DESKTOP_LOGO_ROW in parent. */
function AnimatedDesktopLogoRow({
  className,
  logos,
  isColored,
  numLogos,
}: {
  className: string;
  logos: LogoEntry[];
  isColored: boolean;
  numLogos: number;
}) {
  const prefersReducedMotion = useReducedMotion();

  const [numVisibleLogos, setNumVisibleLogos] = useState(numLogos);
  const [logoIndices, setLogoIndices] = useState<number[]>(() =>
    Array.from({ length: numLogos }, (_, i) => i)
  );

  const popLayoutStyleHostRef = useRef<HTMLDivElement>(null);
  const [popLayoutStyleRoot, setPopLayoutStyleRoot] =
    useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    const el = popLayoutStyleHostRef.current;
    if (el) setPopLayoutStyleRoot(el);
  }, []);

  const generateUniqueIndices = useCallback(
    (count: number) => {
      const totalLogos = numLogos * 3;
      const indices: number[] = [];
      const usedLogos = new Set<number>();

      for (let i = 0; i < count; i++) {
        let newIndex;
        let attempts = 0;
        do {
          newIndex = Math.floor(Math.random() * totalLogos);
          attempts++;
        } while (attempts < 100 && usedLogos.has(newIndex % numLogos));

        indices.push(newIndex);
        usedLogos.add(newIndex % numLogos);
      }
      return indices;
    },
    [numLogos]
  );

  useEffect(() => {
    const applyViewport = () => {
      const newNumVisible = Math.floor(window.innerWidth / LOGOS_WIDTH);
      setNumVisibleLogos((prev) => (prev !== newNumVisible ? newNumVisible : prev));
      setLogoIndices(generateUniqueIndices(newNumVisible));
    };

    applyViewport();

    window.addEventListener("resize", applyViewport);
    return () => window.removeEventListener("resize", applyViewport);
  }, [generateUniqueIndices]);

  useEffect(() => {
    if (prefersReducedMotion) return;

    const totalLogos = numLogos * 3;

    const interval = setInterval(() => {
      setLogoIndices((prev) => {
        const newIndices: number[] = [];
        const usedLogos = new Set<number>();

        for (let i = 0; i < prev.length; i++) {
          let newLogoIndex;
          let attempts = 0;
          const maxAttempts = 100;

          do {
            newLogoIndex = Math.floor(Math.random() * totalLogos);
            attempts++;
          } while (
            attempts < maxAttempts &&
            (newLogoIndex === prev[i] ||
              usedLogos.has(newLogoIndex % numLogos))
          );

          newIndices.push(newLogoIndex);
          usedLogos.add(newLogoIndex % numLogos);
        }

        return newIndices;
      });
    }, 2500);

    return () => {
      clearInterval(interval);
    };
  }, [numLogos, numVisibleLogos, prefersReducedMotion]);

  return (
    <div className={className}>
      <div
        ref={popLayoutStyleHostRef}
        aria-hidden
        className="sr-only"
        data-framer-poplayout-style-root=""
      />
      <div className="flex overflow-hidden">
        {logoIndices.map((logoIndex, positionIndex) => {
          const logo = logos[logoIndex];
          return (
            <div
              key={positionIndex}
              className="flex-1 border-y border-r last:border-r-0 border-border bg-background/40 relative overflow-hidden"
              data-tina-field={tinaFieldSafe(logo, "image")}
            >
              <AnimatePresence
                mode="popLayout"
                root={popLayoutStyleRoot ?? undefined}
              >
                <motion.div
                  key={logoIndex}
                  initial={
                    prefersReducedMotion
                      ? undefined
                      : { opacity: 0, filter: "blur(8px)", y: -20 }
                  }
                  animate={
                    prefersReducedMotion
                      ? { opacity: 1 }
                      : { opacity: 1, filter: "blur(0px)", y: 0 }
                  }
                  exit={
                    prefersReducedMotion
                      ? undefined
                      : { opacity: 0, filter: "blur(8px)", y: 20 }
                  }
                  transition={{
                    duration: prefersReducedMotion ? 0 : 0.5,
                    delay: prefersReducedMotion ? 0 : positionIndex * 0.1,
                  }}
                  className="w-full h-full aspect-[16/7] flex items-center justify-center p-4"
                >
                  {logo?.image && (
                    <NormalizedLogo
                      src={logo.image}
                      className={cn(
                        "w-auto h-auto",
                        {
                          "[&_*]:!fill-current":
                            !isColored &&
                            !NO_FILL_LOGOS.includes(logo?.altText ?? ""),
                        }
                      )}
                      title={logo?.altText ?? undefined}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const LogosCarousel = ({
  backgroundOnScroll = false,
}: {
  backgroundOnScroll?: boolean;
}) => {
  const globalData = useGlobalData();
  const isColored = false;
  const [backgroundColor, setBackgroundColor] = useState("bg-background/0");

  const logosData = globalData?.logos;
  const logos = [...logosData.logos, ...logosData.logos, ...logosData.logos];

  const numLogos = logosData?.logos?.length || 8;

  const autoplay = useRef(
    Autoplay({ delay: 0, stopOnInteraction: false, stopOnFocusIn: false })
  );

  const { scrollY } = useScroll();
  const y = useMotionValue(0);

  const opacity = useMotionValue(1);

  useMotionValueEvent(scrollY, "change", (latest) => {
    y.set(latest);
    opacity.set(1 - latest / 20);

    if (latest > 100) {
      setBackgroundColor(`bg-background`);
    } else {
      setBackgroundColor("bg-background/0");
    }
  });

  const stripClassName = cn(
    "hidden lg:block w-full transition-colors duration-700",
    backgroundOnScroll ? backgroundColor : "bg-background/20"
  );

  return (
    <div className="-mt-[90px] md:-mt-[134px] relative z-10">
      <motion.div
        className="hidden lg:block content text-xs text-balance md:text-base text-muted-foreground font-mono uppercase tracking-wider select-none mb-4"
        style={{
          y: backgroundOnScroll ? y : 0,
          opacity: backgroundOnScroll ? opacity : 1,
        }}
      >
        <div
          className="w-auto"
          data-tina-field={tinaFieldSafe(logosData, "title")}
        >
          {logosData?.title}
        </div>
      </motion.div>

      {USE_ANIMATED_DESKTOP_LOGO_ROW && (
        <AnimatedDesktopLogoRow
          className={stripClassName}
          logos={logos}
          isColored={isColored}
          numLogos={numLogos}
        />
      )}

      <Carousel
        opts={{
          align: "start",
          loop: true,
          duration: 40000,
        }}
        className={cn(
          USE_ANIMATED_DESKTOP_LOGO_ROW
            ? "block lg:hidden w-full cursor-grab active:cursor-grabbing transition-colors duration-700"
            : "block w-full cursor-grab active:cursor-grabbing transition-colors duration-700",
          backgroundOnScroll ? backgroundColor : "bg-background/20"
        )}
        plugins={[autoplay.current]}
      >
        <CarouselContent className="">
          {logos?.map((logo, index) => (
            <CarouselItem
              key={index}
              className={cn(
                "pl-0 border-y border-r border-border bg-background/40",
                "basis-[30%] md:basis-[20%] lg:basis-[210px]"
              )}
            >
              <div
                className="w-full h-full aspect-[16/7] flex items-center justify-center p-4 sm:p-6"
                data-tina-field={tinaFieldSafe(logo, "image")}
              >
                {logo?.image && (
                  <NormalizedLogo
                    src={logo?.image}
                    className={cn("w-auto h-auto max-sm:!max-h-5", {
                      "[&_*]:!fill-current":
                        !isColored && !NO_FILL_LOGOS.includes(logo?.altText),
                      "max-sm:!max-h-8": NO_MAX_HEIGHT_LOGOS.includes(logo?.altText),
                    })}
                    title={logo?.altText}
                  />
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div >
  );
};

export default LogosCarousel;
